import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProductGrid.css';
import { db } from '../firebase/config';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import LazyImage from './LazyImage';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

const DEFAULT_PRODUCT_IMAGE = 'https://via.placeholder.com/600x800?text=Abaya';

const optimizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  return getOptimizedImageUrl(url, 'card');
};

const getProductImages = (product) => {
  const images = [];
  if (Array.isArray(product.images)) {
    product.images.forEach((img) => {
      const trimmed = img ? img.trim() : '';
      if (trimmed && !images.includes(trimmed)) {
        images.push(optimizeImageUrl(trimmed));
      }
    });
  }
  if (product.image) {
    const trimmed = product.image.trim();
    if (trimmed && !images.includes(trimmed)) {
      images.push(optimizeImageUrl(trimmed));
    }
  }
  // Also check for image2, image3, image4 for backward compatibility
  [product.image2, product.image3, product.image4].forEach(img => {
    if (img) {
      const trimmed = img.trim();
      if (trimmed && !images.includes(trimmed)) {
        images.push(optimizeImageUrl(trimmed));
      }
    }
  });
  return images;
};

// Product Card Component with Image Hover Effect
function ProductCard({ product, images, defaultImage, onClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const cardRef = useRef(null);
  const touchStartX = useRef(null);
  const hasMoved = useRef(false);
  const availabilityLabelRaw = product.availability || 'In Stock';
  const availabilityKey = availabilityLabelRaw.toLowerCase();
  const availabilityMarkedLow = availabilityKey.includes('few') || availabilityKey.includes('low') || availabilityKey.includes('left');
  const trackQuantity = product.trackQuantity !== false;
  const lowStockThreshold = Number(product.lowStockThreshold ?? 10);
  const stockValueRaw = product.stock ?? product.quantity;
  const stockValue = Number.isFinite(Number(stockValueRaw)) ? Number(stockValueRaw) : null;
  const isOut = availabilityKey.includes('out') || availabilityKey.includes('sold') || (trackQuantity && stockValue !== null && stockValue <= 0);
  const isLowStock = !isOut && (availabilityMarkedLow || (trackQuantity && stockValue !== null && stockValue > 0 && stockValue <= lowStockThreshold));
  const availabilityLabel = isOut ? 'Sold Out' : 'Few Pieces Left';
  const availabilityClass = isOut ? 'is-out' : 'is-low';

  const handleMouseMove = (e) => {
    if (images.length <= 1) return;
    
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const cardWidth = rect.width;
    const relativeX = e.clientX - rect.left;
    const percentage = relativeX / cardWidth;
    
    // Calculate which image to show based on mouse position
    const newIndex = Math.min(
      Math.floor(percentage * images.length),
      images.length - 1
    );
    
    if (newIndex !== currentImageIndex) {
      setCurrentImageIndex(newIndex);
    }
  };

  const handleMouseLeave = () => {
    setCurrentImageIndex(0);
  };

  const handleTouchStart = (e) => {
    if (images.length <= 1) return;
    touchStartX.current = e.touches[0].clientX;
    hasMoved.current = false;
  };

  const handleTouchMove = (e) => {
    if (images.length <= 1 || !touchStartX.current) return;
    
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const currentX = e.touches[0].clientX;
    const moveDistance = Math.abs(currentX - touchStartX.current);
    
    // If moved more than 5px, consider it a swipe
    if (moveDistance > 5) {
      hasMoved.current = true;
    }
    
    const cardWidth = rect.width;
    const relativeX = currentX - rect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / cardWidth));
    
    // Calculate which image to show based on touch position
    const newIndex = Math.min(
      Math.floor(percentage * images.length),
      images.length - 1
    );
    
    if (newIndex !== currentImageIndex) {
      setCurrentImageIndex(newIndex);
    }
  };

  const handleTouchEnd = (e) => {
    // If user swiped, prevent click navigation
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
    }
    touchStartX.current = null;
    hasMoved.current = false;
  };

  const handleClick = (e) => {
    // Prevent navigation if it was a swipe
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick();
  };

  const currentImage = images[currentImageIndex] || defaultImage;

  return (
    <div 
      ref={cardRef}
      className="product-card"
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="product-card__image-wrapper">
        <LazyImage
          src={currentImage}
          className="product-card__image"
          context="card"
        />
        {(isOut || isLowStock) && (
          <span className={`product-card__availability ${availabilityClass}`}>
            {availabilityLabel}
          </span>
        )}
        {images.length > 1 && (
          <div className="product-card__image-indicator">
            {images.map((_, index) => (
              <span
                key={index}
                className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="product-card__info">
        <span className="product-card__code">{product.name}</span>
        <span className="product-card__price">{product.price.toFixed(2)} ر.ق</span>
      </div>
    </div>
  );
}

function SectionHeader() {
  const [sectionContent, setSectionContent] = useState({
    trendyTitle: 'TRENDY ITEMS',
    trendySubtitle: 'DISCOVER OUR LATEST COLLECTION OF PREMIUM ABAYAS',
  });

  useEffect(() => {
    const fetchSectionContent = async () => {
      try {
        const homepageRef = doc(db, 'homepage', 'content');
        const docSnap = await getDoc(homepageRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSectionContent({
            trendyTitle: data.trendyTitle || sectionContent.trendyTitle,
            trendySubtitle: data.trendySubtitle || sectionContent.trendySubtitle,
          });
        }
      } catch (error) {
        console.error('Error fetching section content:', error);
      }
    };
    fetchSectionContent();
  }, []);

  return (
    <div className="section-header">
      <h2 className="section-title">{sectionContent.trendyTitle}</h2>
      <p className="section-subtitle">{sectionContent.trendySubtitle}</p>
    </div>
  );
}

function ProductGrid() {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      setIsLoading(true);
      try {
        const productsRef = collection(db, 'products');
        let snapshot;
        
        try {
          // Try with orderBy
          const q = query(
            productsRef,
            where('featured', '==', true),
            orderBy('createdAt', 'desc'),
            limit(4)
          );
          snapshot = await getDocs(q);
        } catch (orderError) {
          // If orderBy fails, try without it
          console.warn('Fetching without orderBy:', orderError);
          const q = query(
            productsRef,
            where('featured', '==', true),
            limit(4)
          );
          snapshot = await getDocs(q);
        }
        
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFeaturedProducts(products);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        // Fallback: try to get any products
        try {
          const productsRef = collection(db, 'products');
          const snapshot = await getDocs(query(productsRef, limit(4)));
          const products = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFeaturedProducts(products);
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  const handleViewAll = () => {
    navigate('/shop');
  };

  return (
    <section className="product-section" id="abayas">
      <div className="container">
        <SectionHeader />

          {isLoading ? (
            <div className="product-grid product-grid--skeleton" aria-hidden="true">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`featured-skeleton-${index}`} className="product-card skeleton-card">
                  <div className="product-card__image skeleton"></div>
                  <div className="product-card__info">
                    <span className="skeleton skeleton-line"></span>
                    <span className="skeleton skeleton-line skeleton-line--short"></span>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProducts.length === 0 ? (
            <p className="no-products-text">No featured products yet.</p>
          ) : (
            <div className="product-grid">
              {featuredProducts.map(product => {
                const images = getProductImages(product);
                const imageUrl = images[0] || DEFAULT_PRODUCT_IMAGE;
                
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    images={images}
                    defaultImage={imageUrl || DEFAULT_PRODUCT_IMAGE}
                    onClick={() => handleProductClick(product)}
                  />
                );
              })}
            </div>
          )}

        <div className="load-more-section">
          <button 
            className="btn btn-outline" 
            onClick={handleViewAll}
          >
            Load More...
          </button>
        </div>
      </div>
    </section>
  );
}

export default ProductGrid;
